import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, Heart, DollarSign } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
}

const CustomerRetentionValuePage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/customer-retention-value`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Customer Retention & Value Calculators | Free Marketing Tools</title>
        <meta name="description" content="Calculate customer lifetime value, retention rates, order values & more with our free calculators. Optimize your customer retention strategy." />
        <meta name="keywords" content="customer lifetime value calculator, retention rate calculator, average order value, cart value calculator" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Customer Retention & Value Calculators" />
        <meta property="og:description" content="Calculate and improve customer retention metrics with our free calculators. Measure CLV, retention rates, and more." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Customer Retention & Value Calculators" />
        <meta property="twitter:description" content="Calculate and improve customer retention metrics with our free calculators. Measure CLV, retention rates, and more." />
        <meta property="twitter:image" content={logoUrl} />
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Customer Retention & Value</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Customer Retention & Value Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard 
            title="Customer Lifetime Value" 
            description="Calculate the total value of a customer over their relationship with your business" 
            link="/calculators/customer-lifetime-value" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Customer Retention Rate" 
            description="Measure the percentage of customers you retain over a given period" 
            link="/calculators/customer-retention-rate" 
            icon={Heart}
          />
          <CalculatorCard 
            title="Average Customer Lifespan" 
            description="Calculate how long a customer typically stays with your business" 
            link="/calculators/average-customer-lifespan" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Average Order Value" 
            description="Determine the average amount spent per order" 
            link="/calculators/average-order-value" 
            icon={DollarSign}
          />
          <CalculatorCard 
            title="Average Cart Value" 
            description="Calculate the average value of items in a customer's cart" 
            link="/calculators/average-cart-value" 
            icon={DollarSign}
          />
          <CalculatorCard 
            title="Cart Abandonment Rate" 
            description="Measure the rate at which customers abandon their shopping carts" 
            link="/calculators/cart-abandonment-rate" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Loyalty Program ROI" 
            description="Calculate the return on investment for your loyalty program" 
            link="/calculators/loyalty-program-roi" 
            icon={TrendingUp}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Understanding Customer Retention & Value</h2>
        <p className="text-gray-600 mb-4">
          Customer retention and value metrics help you understand the long-term impact of your customer relationships. Use these calculators to measure and optimize your retention strategy.
        </p>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link, icon: Icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <Icon className="w-8 h-8 text-indigo-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default CustomerRetentionValuePage;
